import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { aiApi } from '../../api/ai';
import type { GroupSuggestion } from '../../api/ai';
import { groupsApi } from '../../api/groups';
import { useQueryClient } from '@tanstack/react-query';

interface AiGroupSuggestionsProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AiGroupSuggestions({ isOpen, onClose }: AiGroupSuggestionsProps) {
  const queryClient = useQueryClient();
  const [scenario, setScenario] = useState('');
  const [groupingBy, setGroupingBy] = useState('scenario');
  const [maxGroups, setMaxGroups] = useState(5);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState<GroupSuggestion[]>([]);
  const [creating, setCreating] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!scenario.trim()) {
      setError('Describe the scenario for grouping.');
      return;
    }
    setLoading(true);
    setError('');
    setSuggestions([]);
    try {
      const res = await aiApi.suggestGroups({
        scenario,
        grouping_by: groupingBy,
        max_groups: maxGroups,
      });
      setSuggestions(res.data.groups || []);
      if (!res.data.groups?.length) {
        setError('No group suggestions generated. Try a different scenario description.');
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message || 'Failed to generate suggestions. Check your AI settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async (suggestion: GroupSuggestion) => {
    setCreating(suggestion.name);
    try {
      await groupsApi.create({
        name: suggestion.name,
        targets: suggestion.emails.map((email, i) => ({
          email,
          first_name: '',
          last_name: '',
          position: suggestion.positions[i] || '',
        })),
      });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      setSuggestions((prev) => prev.filter((s) => s.name !== suggestion.name));
    } catch {
      setError(`Failed to create group "${suggestion.name}".`);
    } finally {
      setCreating(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="AI Group Suggestions" size="lg">
      <div className="space-y-4">
        <div>
          <label className="label" htmlFor="ai-scenario">Scenario Description</label>
          <textarea
            id="ai-scenario"
            className="input min-h-[100px]"
            value={scenario}
            onChange={(e) => setScenario(e.target.value)}
            placeholder="Describe the phishing scenario (e.g., 'Finance-targeted invoice fraud campaign targeting employees who handle payments')"
            rows={3}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label" htmlFor="ai-grouping-by">Group by</label>
            <select
              id="ai-grouping-by"
              className="input"
              value={groupingBy}
              onChange={(e) => setGroupingBy(e.target.value)}
            >
              <option value="scenario">Scenario relevance</option>
              <option value="position">Position/role</option>
              <option value="risk">Risk profile</option>
            </select>
          </div>
          <div>
            <label className="label" htmlFor="ai-max-groups">Max groups</label>
            <input
              id="ai-max-groups"
              type="number"
              className="input"
              min={2}
              max={20}
              value={maxGroups}
              onChange={(e) => setMaxGroups(Number(e.target.value))}
            />
          </div>
        </div>

        {error && (
          <p role="alert" className="text-sm text-red-600">{error}</p>
        )}

        <Button onClick={handleGenerate} loading={loading}>
          <Sparkles className="w-4 h-4 mr-2" />
          Generate Suggestions
        </Button>

        {suggestions.length > 0 && (
          <div className="space-y-3 mt-4 pt-4 border-t border-gray-200">
            <h4 className="font-semibold text-gray-900">Suggested Groups</h4>
            {suggestions.map((s) => (
              <div key={s.name} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900">{s.name}</p>
                    <p className="text-sm text-gray-600 mt-1">{s.rationale}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {s.emails.length} members
                      {s.positions.length > 0 && (
                        <span> &middot; Positions: {s.positions.join(', ')}</span>
                      )}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleCreateGroup(s)}
                    loading={creating === s.name}
                  >
                    Create
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
